'use client';

import React, { useState, useEffect, useRef } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import JourneyNav from '@/components/JourneyNav';
import LetterHero from './components/LetterHero';
import { LetterBody, LetterBridges } from './components/LetterComponents';
import LetterProgressBar from '@/components/LetterProgressBar';
import Plot3SidebarTrail from './components/Plot3SidebarTrail';
import Plot4Content from './components/Plot4Content';
import Plot4ArRahmanJibril from './components/Plot4ArRahmanJibril';
import Plot4MissingContent from './components/Plot4MissingContent';
import Plot4AlKhabirZahir from './components/Plot4AlKhabirZahir';
import RoleModulesSection from './components/RoleModulesSection';
import { HikmahStationsGrid, LayerSyncDisplay } from './components/SharedHikmahSystem';
import Plot4YaqeenContent from './components/Plot4YaqeenContent';
import RolePublishingPanel from './components/RolePublishingPanel';
import Plot4Enhancements from './components/Plot4Enhancements';
import Plot5AuditEnhancements from './components/Plot5AuditEnhancements';
import VocationalNetwork from './components/VocationalNetwork';
import DailyQuestGenerator from './components/DailyQuestGenerator';
import SynthesisPanelIsnad from './components/SynthesisPanelIsnad';
import ReadingModeWrapper from './components/ReadingMode';
import SectionManagementPanel from './components/SectionManagementPanel';
import DraggableSectionManager from './components/DraggableSectionManager';
import GardenWorkshop from './components/GardenWorkshop';
import EmbedCodeGenerator from './components/EmbedCodeGenerator';
import PublicGardenGallery from './components/PublicGardenGallery';
import LetterReconstructionScaffold from './components/LetterReconstructionScaffold';
import LetterSectionThematicInterface from './components/LetterSectionThematicInterface';
import {
  RootModulesArchitecture,
  VocationLensModule,
  VocationalConnectionsMap,
  IRLLensSelfReferential,
} from './components/Plot5Enhancements';

// ─── SECTION TITLES (all 41) ──────────────────────────────────────────────────
const SECTION_TITLES: string[] = [
  'The Occurance', 'Caretaker', 'The Mural', "ROOTS Ma'rifah",
  'Al-Qadir (The All-Powerful)', 'Aql', 'Ilm', '[Entering the Haal Platform]',
  'Mend', 'Fitrah', 'Tawbah', 'Sabr', 'Shukr', 'Tawakkul', 'Ikhlas',
  'Al-Baseer (The All-Seeing)', 'Al-Sami (The All-Hearing)', 'Al-Latif (The Subtle)',
  'Al-Khabir (The All-Aware)', 'Al-Haqq (The Truth)', 'Al-Khaliq (The Creator)',
  'Al-Muhyyi (The Bestower of Life)', 'Al-Mumit (The Bringer of Death)',
  'Al-Ghafur (The Oft-Forgiving)', 'Al-Tawwab (The Accepter of Repentance)',
  'Al-Wadud (The Most Loving)', 'Al-Rahim (The Most Compassionate)',
  'Al-Rahman (The Most Merciful)', 'Al-Razzaq (The Provider)',
  'Al-Wahhab (The Bestower)', 'Al-Karim (The Most Generous)',
  'Al-Adl (The Just)', 'Al-Hakam (The Judge)', 'Al-Muqsit (The Equitable)',
  'Al-Wali (The Protecting Friend)', 'Al-Mujib (The Responsive)',
  'Al-Qarib (The Near)', 'Al-Shahid (The Witness)',
  'Al-Hafiz (The Preserver)', 'Al-Wakil (The Trustee)', 'Al-Ahad (The One)',
];

// ─── MOBILE SECTIONS PANEL ────────────────────────────────────────────────────
function MobileSectionsPanel() {
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);

  // Track which section is in view
  useEffect(() => {
    const handleScroll = () => {
      for (let i = SECTION_TITLES.length; i >= 1; i--) {
        const el = document.getElementById(`letter-section-${i}`);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= window.innerHeight * 0.4) {
            setActiveSection(i - 1);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close panel when clicking outside
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const navigateTo = (idx: number) => {
    const el = document.getElementById(`letter-section-${idx + 1}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setOpen(false);
  };

  const progress = ((activeSection + 1) / SECTION_TITLES.length) * 100;

  return (
    <div
      ref={panelRef}
      style={{
        position: 'fixed',
        bottom: 20,
        right: 16,
        zIndex: 500,
        display: 'none', // shown via CSS media query below
      }}
      className="mobile-sections-widget"
    >
      <style>{`
        @media (max-width: 640px) {
          .mobile-sections-widget { display: block !important; }
        }
      `}</style>

      {/* Expanded panel */}
      {open && (
        <div style={{
          position: 'absolute',
          bottom: 56,
          right: 0,
          width: 260,
          maxHeight: '65vh',
          background: 'rgba(6,13,18,0.98)',
          border: '1px solid rgba(201,168,76,0.2)',
          borderRadius: 10,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
        }}>
          {/* Header */}
          <div style={{
            padding: '12px 14px 10px',
            borderBottom: '1px solid rgba(201,168,76,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}>
            <span style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.7)' }}>
              ◈ Sections
            </span>
            <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.35)', letterSpacing: '.06em' }}>
              {activeSection + 1} / {SECTION_TITLES.length}
            </span>
          </div>

          {/* Progress bar */}
          <div style={{ height: 2, background: 'rgba(201,168,76,0.08)', flexShrink: 0 }}>
            <div style={{ height: '100%', background: '#c9a84c', width: `${progress}%`, transition: 'width 0.3s ease' }} />
          </div>

          {/* Section list */}
          <div style={{ overflowY: 'auto', flex: 1, padding: '6px 0' }}>
            {SECTION_TITLES.map((title, idx) => {
              const isActive = idx === activeSection;
              const isRead = idx < activeSection;
              return (
                <button
                  key={idx}
                  onClick={() => navigateTo(idx)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '7px 14px',
                    background: isActive ? 'rgba(201,168,76,0.08)' : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    borderLeft: isActive ? '2px solid #c9a84c' : '2px solid transparent',
                    transition: 'all 0.15s',
                  }}
                >
                  {/* Circle indicator */}
                  <div style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    flexShrink: 0,
                    background: isActive ? '#c9a84c' : isRead ? 'rgba(201,168,76,0.4)' : 'transparent',
                    border: isActive ? '2px solid #c9a84c' : isRead ? '2px solid rgba(201,168,76,0.4)' : '2px solid rgba(201,168,76,0.2)',
                    transition: 'all 0.2s',
                  }} />
                  {/* Number */}
                  <span style={{
                    fontFamily: 'monospace',
                    fontSize: 7,
                    color: isActive ? 'rgba(201,168,76,0.6)' : 'rgba(212,197,169,0.25)',
                    letterSpacing: '.06em',
                    flexShrink: 0,
                    minWidth: 18,
                  }}>
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  {/* Title */}
                  <span style={{
                    fontFamily: "'Georgia', serif",
                    fontSize: 11,
                    color: isActive ? '#F5F0E8' : isRead ? 'rgba(212,197,169,0.55)' : 'rgba(212,197,169,0.35)',
                    lineHeight: 1.3,
                    transition: 'color 0.15s',
                  }}>
                    {title}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Floating trigger button */}
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          background: open ? 'rgba(201,168,76,0.15)' : 'rgba(6,13,18,0.95)',
          border: `1px solid ${open ? 'rgba(201,168,76,0.5)' : 'rgba(201,168,76,0.25)'}`,
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
          boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
          position: 'relative',
        }}
        aria-label="Open sections navigation"
      >
        {/* Mini progress ring overlay */}
        <svg
          width={48}
          height={48}
          style={{ position: 'absolute', top: 0, left: 0, transform: 'rotate(-90deg)' }}
        >
          <circle cx={24} cy={24} r={22} fill="none" stroke="rgba(201,168,76,0.12)" strokeWidth={2} />
          <circle
            cx={24} cy={24} r={22}
            fill="none"
            stroke="#c9a84c"
            strokeWidth={2}
            strokeDasharray={`${2 * Math.PI * 22}`}
            strokeDashoffset={`${2 * Math.PI * 22 * (1 - progress / 100)}`}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.4s ease' }}
          />
        </svg>
        <span style={{ fontFamily: 'monospace', fontSize: 7, color: '#c9a84c', letterSpacing: '.06em', position: 'relative', zIndex: 1 }}>
          {String(activeSection + 1).padStart(2, '0')}
        </span>
        <span style={{ fontFamily: 'monospace', fontSize: 6, color: 'rgba(201,168,76,0.5)', letterSpacing: '.04em', position: 'relative', zIndex: 1 }}>
          §
        </span>
      </button>
    </div>
  );
}

// ─── EXTENDED CONTENT LAYERS ──────────────────────────────────────────────────
// All existing letter content preserved as collapsible expansion panels.
// These attach below the reconstructed letter body.

function ExtendedContentLayers() {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ background: '#060d12', borderTop: '1px solid rgba(201,168,76,0.08)' }}>
      {/* Toggle */}
      <div className="max-w-[900px] mx-auto px-6 md:px-10 py-8">
        <button
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between px-5 py-4 rounded-xl transition-colors"
          style={{
            background: 'rgba(201,168,76,0.04)',
            border: '1px solid rgba(201,168,76,0.12)',
            color: 'rgba(201,168,76,0.65)',
          }}
        >
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs uppercase tracking-widest">◈ Extended Content &amp; Expansion Panels</span>
            <span className="font-mono text-xs" style={{ color: 'rgba(201,168,76,0.3)' }}>
              Platform callouts · Audit layers · Framework modules · Role lenses
            </span>
          </div>
          <svg
            className={`w-4 h-4 flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Expanded layers */}
      {open && (
        <>
          <LetterBody />

          <Plot4Content />
          <Plot4ArRahmanJibril />
          <Plot4MissingContent />
          <Plot4AlKhabirZahir />
          <Plot4YaqeenContent />
          <Plot4Enhancements />
          <VocationalNetwork />
          <SynthesisPanelIsnad />
          <DailyQuestGenerator />

          <section id="section-plot5" style={{ background: 'rgba(6,13,18,0.97)', borderTop: '1px solid rgba(201,168,76,0.08)', padding: '80px 0' }}>
            <div className="max-w-[900px] mx-auto px-6 md:px-10">
              <div className="mb-10">
                <p className="font-mono text-[9px] tracking-widest uppercase mb-4" style={{ color: 'rgba(139,105,20,0.6)' }}>
                  ✦ Plot 5 — Root Modules · Vocation Lens · Revelation Language
                </p>
                <h2 className="font-mono text-2xl font-black uppercase tracking-tight mb-4" style={{ color: '#F5F0E8' }}>
                  The Root is the Seed
                </h2>
                <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 13, lineHeight: 1.7, maxWidth: 600, marginBottom: 8 }}>
                  A single Arabic root contains an entire module taxonomy. The twelve meanings of ق-د-ر map to twelve different functions of the garden. Plant the root; grow the cluster.
                </p>
                <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 12, lineHeight: 1.7, maxWidth: 600, fontStyle: 'italic', marginBottom: 32 }}>
                  "You recognized what the Pen wrote. Your observation adds to the communal tafsir of the Qadr." — Growth is revelation, not production. The garden uncovers what was always written.
                </p>
              </div>
              <div style={{ marginBottom: 40 }}>
                <RootModulesArchitecture />
              </div>
              <div style={{ marginBottom: 40 }}>
                <IRLLensSelfReferential />
              </div>
              <div style={{ marginBottom: 40 }}>
                <div style={{ marginBottom: 16 }}>
                  <p className="font-mono text-[9px] tracking-widest uppercase mb-2" style={{ color: 'rgba(201,168,76,0.5)' }}>
                    ◈ Vocation Lens Module
                  </p>
                  <h3 className="font-mono text-xl font-black uppercase tracking-tight mb-3" style={{ color: '#F5F0E8' }}>
                    What Would It Look Like?
                  </h3>
                  <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 13, lineHeight: 1.7, maxWidth: 600 }}>
                    "What if we modeled all the possible roles of our current society, redeveloping and recognizing those roles with the primary purpose being oriented toward the remembrance of Allah?" The same methodology, filtered through your specific work.
                  </p>
                </div>
                <VocationLensModule />
              </div>
              <div>
                <div style={{ marginBottom: 16 }}>
                  <p className="font-mono text-[9px] tracking-widest uppercase mb-2" style={{ color: 'rgba(45,122,71,0.6)' }}>
                    ⬡ Vocational Connections
                  </p>
                  <h3 className="font-mono text-xl font-black uppercase tracking-tight mb-3" style={{ color: '#F5F0E8' }}>
                    The Roles Are a System
                  </h3>
                  <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 13, lineHeight: 1.7, maxWidth: 600 }}>
                    "The funding structure that sustains the university hosts the classes that produce the law student who drafts the legislation that addresses the concerns that the janitor identified." The mycorrhizal network between gardens is composed of vocational connections.
                  </p>
                </div>
                <VocationalConnectionsMap />
              </div>
            </div>
          </section>

          <Plot5AuditEnhancements />
          <RoleModulesSection />

          <section id="section-role-publishing" style={{ background: 'rgba(6,13,18,0.97)', borderTop: '1px solid rgba(201,168,76,0.08)', padding: '80px 0' }}>
            <div className="max-w-[900px] mx-auto px-6 md:px-10">
              <div className="mb-8">
                <p className="font-mono text-[9px] tracking-widest uppercase mb-4" style={{ color: 'rgba(139,105,20,0.6)' }}>
                  ◈ Community Role Library · Publish &amp; Remix
                </p>
                <h2 className="font-mono text-2xl font-black uppercase tracking-tight mb-2" style={{ color: '#F5F0E8' }}>
                  Publish a Role Lens
                </h2>
                <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 13, lineHeight: 1.7, maxWidth: 600 }}>
                  "What if we modeled all the possible roles of our current society, redeveloping and recognizing those roles with the primary purpose being oriented toward the remembrance of Allah?" The community creates the role lenses. The platform provides the method.
                </p>
              </div>
              <RolePublishingPanel />
            </div>
          </section>

          <section id="section-hikmah-layer-sync" style={{ background: 'rgba(6,13,18,0.97)', borderTop: '1px solid rgba(201,168,76,0.08)', padding: '80px 0' }}>
            <div className="max-w-[900px] mx-auto px-6 md:px-10">
              <div className="mb-10">
                <p className="font-mono text-[9px] tracking-widest uppercase mb-4" style={{ color: 'rgba(139,105,20,0.6)' }}>
                  ◈ Shared Component Library · Hikmah Stations &amp; Layer Sync
                </p>
                <h2 className="font-mono text-2xl font-black uppercase tracking-tight mb-4" style={{ color: '#F5F0E8' }}>
                  Hikmah Stations at Biome Transitions
                </h2>
                <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 13, lineHeight: 1.7, maxWidth: 600, marginBottom: 32 }}>
                  Hikmah Stations appear at biome transitions and depth transitions — not as lessons but as constellations of connections that gesture at purpose.
                </p>
                <HikmahStationsGrid />
              </div>
              <div className="mt-12">
                <h3 className="font-mono text-lg font-black uppercase tracking-tight mb-4" style={{ color: '#F5F0E8' }}>
                  Layer Numbering Sync
                </h3>
                <LayerSyncDisplay />
              </div>
            </div>
          </section>

          <LetterBridges />
        </>
      )}
    </div>
  );
}

export default function OpenLetterPage() {
  return (
    <ReadingModeWrapper>
      <div style={{ minHeight: '100vh', background: '#060d12', color: '#d4c5a9', overflowX: 'hidden', maxWidth: '100vw' }}>
      <style>{`
        /* ── Desktop grid layouts ─────────────────────────────────── */
        .letter-section-grid {
          grid-template-columns: 1fr 280px;
        }
        .letter-bridges-grid {
          grid-template-columns: repeat(3, 1fr);
        }
        .paired-names-grid {
          grid-template-columns: 1fr 1fr;
        }
        .ihsan-grid {
          grid-template-columns: repeat(3, 1fr);
        }

        /* ── Responsive: collapse on small screens ── */
        @media (max-width: 900px) {
          .letter-section-grid {
            grid-template-columns: 1fr !important;
          }
          .letter-bridges-grid {
            grid-template-columns: 1fr !important;
          }
          .paired-names-grid {
            grid-template-columns: 1fr !important;
          }
          .ihsan-grid {
            grid-template-columns: 1fr !important;
          }
          .glossary-grid {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
          .glossary-terms-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }

        /* ── Mobile: remove right padding from left-column when grid collapses ── */
        @media (max-width: 900px) {
          .letter-section-grid > div:first-child {
            padding-right: 0 !important;
            border-right: none !important;
          }
        }

        /* ── Small screen: always apply mobile styles ─────────────── */
        @media (max-width: 640px) {
          .letter-section-title {
            font-size: clamp(18px, 5vw, 22px) !important;
            line-height: 1.3 !important;
          }
          .letter-arabic-title {
            font-size: clamp(26px, 7vw, 32px) !important;
          }
          .letter-body-text {
            font-size: clamp(14px, 4vw, 15px) !important;
            line-height: 1.85 !important;
          }
          .letter-ayah-text {
            font-size: 13px !important;
            line-height: 1.75 !important;
          }
          .letter-key-phrase {
            font-size: 14px !important;
            padding: 16px !important;
          }
          .letter-section-inner {
            padding-left: clamp(12px, 4vw, 16px) !important;
            padding-right: clamp(12px, 4vw, 16px) !important;
            padding-top: 40px !important;
            padding-bottom: 40px !important;
          }
          .letter-annotation-panel {
            display: none !important;
          }
          .letter-root-arabic {
            font-size: clamp(28px, 8vw, 36px) !important;
            letter-spacing: 8px !important;
          }
          .letter-poem-line-lg {
            font-size: clamp(15px, 4.5vw, 18px) !important;
          }
          .letter-convergence-grid {
            grid-template-columns: 1fr 1fr !important;
          }
          .letter-quote-block {
            padding: 12px 14px !important;
          }
          .letter-special-callout {
            padding: 14px 16px !important;
          }
          .letter-section-number {
            font-size: 9px !important;
          }
          .letter-key-phrase-note {
            font-size: 9px !important;
          }
          .letter-bridges-mobile {
            display: flex !important;
            overflow-x: auto !important;
            gap: 8px !important;
            padding-bottom: 8px !important;
          }
          .letter-bridges-mobile::-webkit-scrollbar {
            height: 2px;
          }
          .letter-bridges-mobile::-webkit-scrollbar-thumb {
            background: rgba(201,168,76,0.2);
          }
          .letter-bridges-section {
            padding: 40px 16px !important;
          }
          .letter-bridges-section-title {
            font-size: 8px !important;
          }
          /* Glossary: single column on small screens */
          .glossary-grid {
            grid-template-columns: 1fr !important;
            gap: 20px !important;
          }
          /* Glossary terms: 1 column on very small screens */
          .glossary-terms-grid {
            grid-template-columns: 1fr !important;
          }
          /* Unstick the glossary left panel on mobile */
          .glossary-grid > div:first-child {
            position: static !important;
          }
          /* Annotation mobile sheet always visible on small screens */
          .letter-mobile-annotation-sheet {
            display: block !important;
            margin-top: 16px;
            border-top: 1px solid rgba(201,168,76,0.1);
            padding-top: 16px;
          }
          .letter-mobile-annotation-toggle {
            display: flex !important;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            background: rgba(201,168,76,0.04);
            border: 1px solid rgba(201,168,76,0.12);
            border-radius: 4px;
            padding: 10px 14px;
            cursor: pointer;
            color: rgba(201,168,76,0.7);
            font-size: 9px;
            letter-spacing: .12em;
            text-transform: uppercase;
            text-align: left;
          }
        }

        /* ── Mobile: annotation panel as bottom sheet ────────────── */
        .letter-mobile-annotation-sheet {
          display: none;
        }

        /* ── Smooth scroll for anchor links ──────────────────────── */
        html {
          scroll-behavior: smooth;
        }

        /* ── Narrow viewport (320px–375px) extra safety ─────────── */
        @media (max-width: 375px) {
          .letter-section-grid,
          .letter-bridges-grid,
          .paired-names-grid,
          .ihsan-grid {
            grid-template-columns: 1fr !important;
          }
          .glossary-terms-grid {
            grid-template-columns: 1fr !important;
          }
          .letter-arabic-title {
            font-size: clamp(22px, 6vw, 28px) !important;
          }
          .letter-body-text {
            font-size: 14px !important;
          }
        }

        /* ── Sidebar trail — hide on small screens ── */
        .plot3-sidebar-trail {
          display: block;
        }
        @media (max-width: 1100px) {
          .plot3-sidebar-trail {
            display: none;
          }
        }
        /* Shift main content right edge when sidebar is visible — use padding so content stays readable */
        @media (min-width: 1101px) {
          .letter-main-content {
            padding-right: 268px;
          }
        }
      `}</style>
      <Header />
      <LetterProgressBar />
      <JourneyNav currentPage="open-letter" />

      <main className="letter-main-content" style={{ overflowX: 'hidden', width: '100%' }}>
        <LetterHero />

        {/* ── LETTER BODY — Reconstruction Scaffold (primary reading experience) ── */}
        <LetterReconstructionScaffold />

        {/* ── EXTENDED CONTENT — expansion panels, platform callouts, audit layers ── */}
        <ExtendedContentLayers />
      </main>

      {/* Persistent Sidebar Trail — desktop only */}
      <div className="plot3-sidebar-trail">
        <Plot3SidebarTrail />
      </div>

      {/* ── MOBILE SECTIONS NAVIGATION WIDGET ── */}
      <MobileSectionsPanel />

      {/* ── DRAGGABLE SECTION MANAGER ── */}
      <DraggableSectionManager />

      {/* ── PUBLIC GARDEN GALLERY ── */}
      <PublicGardenGallery />

      {/* ── GARDEN WORKSHOP ── */}
      <GardenWorkshop />

      {/* ── 41-SECTION THEMATIC INTERFACE ── */}
      <LetterSectionThematicInterface />

      {/* ── EMBED & SHARE TOOLKIT ── */}
      <EmbedCodeGenerator />

      {/* ── SECTION MANAGEMENT PANEL ── */}
      <SectionManagementPanel />

      <Footer />
    </div>
    </ReadingModeWrapper>
  );
}
